import React from 'react';
import PropTypes from 'prop-types';
import DocumentTitle from 'react-document-title';
import CategoryProp from './propTypes/CategoryProp';
import PackProp from './propTypes/PackProp';
import PlanProp from './propTypes/PlanProp';
import CountryProp from '../../commonComponents/forms/Address/CountryProp';
import Notifications from '../../services/notifications';
import Request from '../../services/request';
import translate from '../../commonComponents/translate';
import withCSRF from '../../commonComponents/withCSRF';
import ContentContainer from '../../commonComponents/pageLayout/ContentContainer';
import NavigationMenu from './navigation/NavigationMenu';
import Solutions from './steps/solutions/Solutions';
import BasicFeatures from './steps/options/BasicFeatures';
import AdvancedFeatures from './steps/options/AdvancedFeatures';
import Options from './steps/options/Options';
import Payment from './steps/payment/Payment';
import LicenseProp from './propTypes/LicenseProp';
import BillingProp from '../profile/propTypes/billingProp';
import DiscountProp from './propTypes/DiscountProp';
import TaxIdTypeProp from './propTypes/TaxIdTypeProp';

import {
  isFeatureAvailable,
  isValidPaymentMethod,
  markDiscountApplication,
} from '../../services/cartCalculations';
import {
  compose,
  PRODUCT_TYPE,
  LICENSE_TYPE,
  isHigherAdmin,
} from '../../services/utilityFunctions';
import UserProp from '../profile/propTypes/userProp';
import withLoggedUser from '../../commonComponents/withLoggedUser';
import StripePaymentMethodsProp from '../payments/propTypes/StripePaymentMethodsProp';
import Billing from './steps/billing/Billing';
import BusinessTypeProp from '../profile/propTypes/businessTypeProp';
import Loader from '../../services/loader';

function fillStepsFromProps(
  plan,
  billing = {},
  cart = {},
  plans = [],
  categories = [],
  taxRatePercentage = null,
  paymentMethodType = null,
  paymentMethodId = null,
  plansAreFree = false,
  isResellerInstance = false,
) {
  const { billingAddress = {} } = billing;
  const filledAddress = !!(
    billingAddress?.address_1 &&
    billingAddress?.city &&
    billingAddress?.zipCode &&
    billingAddress?.countryCode
  );
  const filledPayment = isValidPaymentMethod(
    cart,
    plans,
    categories,
    taxRatePercentage,
    plansAreFree,
    paymentMethodType,
    paymentMethodId,
    isResellerInstance,
  );
  if (isResellerInstance) {
    return {
      1: true,
      2: true,
      3: filledPayment,
    };
  }
  return {
    1: !!plan,
    2: true,
    3: true,
    4: true,
    5:
      !!(
        filledAddress &&
        billingAddress?.companyName &&
        billingAddress?.email &&
        billing?.billingLanguage &&
        billing?.businessType &&
        billing?.taxCode
      ) || plansAreFree,
    6: filledPayment,
  };
}

function handleProducts(categories) {
  return categories.reduce(
    (allProducts, c) => allProducts.concat(c.products),
    [],
  );
}

function serializeData(categories) {
  const allProducts = handleProducts(categories);
  const propSubs = allProducts
    .filter(
      p =>
        (p.type === PRODUCT_TYPE.SUBSCRIPTION ||
          p.type === PRODUCT_TYPE.PREMIUM_SUBSCRIPTION) &&
        p.isBought,
    )
    .map(p => ({ product: p, quantity: 1, interval: p.interval }));
  const mandatoryProductsOnCreate = allProducts
    .filter(p => p.isDefaultForSubscription)
    .map(p => ({
      product: { ...p, mandatory: true },
      quantity: 1,
      interval: p.interval,
    }));
  return {
    propSubs,
    mandatoryProductsOnCreate,
  };
}

class SubscriptionDetails extends React.Component {
  constructor(props) {
    super(props);
    const {
      redirectToPaymentSettings,
      license,
      plans,
      categories,
      isResellerInstance,
    } = props;
    const {
      plan: selectedPlan = {},
      paymentMethodType,
      paymentMethodId,
    } = license;
    const { propSubs } = serializeData(categories);
    const plansAreFree =
      license.type === LICENSE_TYPE.TRIAL ||
      license.type === LICENSE_TYPE.FREE ||
      license.type === LICENSE_TYPE.CLOUD_FREE ||
      license.type === LICENSE_TYPE.RESELLER_ARENDT;
    const cart = {
      numberUsers: props.currentUserStats?.totalUsers,
      numberAdmins: props.currentUserStats?.totalAdmins,
      plan: selectedPlan || (plans.length > 0 && plans[0]),
      subscriptions: propSubs,
      packs: [],
      products: [],
      discounts: props.activeDiscount
        ? [{ ...props.activeDiscount, applied: true }]
        : [],
    };
    this.state = {
      postingForm: false,
      showErrors: false,
      selectedIndex: redirectToPaymentSettings ? 6 : 1,
      interval: props.interval,
      selectedPlan: selectedPlan || (plans.length > 0 && plans[0]),
      calculatedUserStats: props.currentUserStats,
      cart,
      billing: {
        billingAddress: { ...props.billing.billingAddress },
        businessType: props.billing.businessType || '',
        invoiceEmail: props.billing.invoiceEmail || '',
        taxId: props.billing.taxId || '',
        taxCode: props.billing.taxCode || '',
        taxType: props.billing.taxType || '',
        billingLanguage: props.billing.billingLanguage || 'en',
      },
      payment: {
        paymentMethods: props.paymentMethods.slice(),
        paymentMethodType: paymentMethodType || '',
        paymentMethodId: paymentMethodId || '',
        accountHolderName: '',
      },
      filledSteps: fillStepsFromProps(
        props.license?.plan,
        props.billing,
        cart,
        props.plans,
        props.categories,
        props.taxRatePercentage,
        paymentMethodType,
        paymentMethodId,
        plansAreFree,
        isResellerInstance,
      ),
      plans: props.plans,
      categories: props.categories,
      packs: props.packs,
      plansAreFree,
    };

    this.currentStep = null;
    this.loader = null;

    this.handleResetErrors = this.handleResetErrors.bind(this);
    this.handlePlanSelection = this.handlePlanSelection.bind(this);
    this.handleUserTotalCalculations =
      this.handleUserTotalCalculations.bind(this);
    this.handlePackAdd = this.handlePackAdd.bind(this);
    this.handlePackRemove = this.handlePackRemove.bind(this);
    this.handleProductAdd = this.handleProductAdd.bind(this);
    this.handleProductRemove = this.handleProductRemove.bind(this);
    this.handleSubscriptionAdd = this.handleSubscriptionAdd.bind(this);
    this.handleSubscriptionRemove = this.handleSubscriptionRemove.bind(this);
    this.handleDiscountAdd = this.handleDiscountAdd.bind(this);
    this.handleDiscountRemove = this.handleDiscountRemove.bind(this);
    this.handleCountryChange = this.handleCountryChange.bind(this);
    this.checkDiscountApplication = this.checkDiscountApplication.bind(this);
    this.handleSubmit = this.handleSubmit.bind(this);
    this.handleNavigation = this.handleNavigation.bind(this);
    this.attemptToNavigate = this.attemptToNavigate.bind(this);
    this.onIntervalChange = this.onIntervalChange.bind(this);
    this.handleRequestError = this.handleRequestError.bind(this);
    this.handlePaymentMethod = this.handlePaymentMethod.bind(this);
    this.handleLoading = this.handleLoading.bind(this);
  }

  componentDidMount() {
    this.loader = new Loader('#subscription_detail_container');
  }

  static getListProducts(interval) {
    return new Promise((resolve, reject) => {
      Request.GET('/shop/products', { interval })
        .done(({ data }) => {
          resolve(data);
        })
        .fail(err => {
          reject(err);
        });
    });
  }

  static getListPlans(interval) {
    return new Promise((resolve, reject) => {
      Request.GET('/shop/plans', { interval })
        .done(({ data }) => {
          resolve(data);
        })
        .fail(err => {
          reject(err);
        });
    });
  }

  handleRequestError(err) {
    const { t } = this.props;
    Notifications.showNotificationError(t('error'), err);
  }

  handleResetErrors() {
    this.setState({ showErrors: false });
  }

  handleNavigation(state, nextIndex, filled, showErrors = false) {
    this.setState(({ selectedIndex, filledSteps }) => ({
      ...state,
      selectedIndex: nextIndex,
      filledSteps: { ...filledSteps, [selectedIndex]: filled },
      showErrors,
    }));
  }

  handleLoading(isLoading) {
    return isLoading ? this.loader.show() : this.loader.hide();
  }

  handlePaymentMethod(type, payload) {
    switch (type) {
      case 'changeTab':
        this.setState(({ payment }) => ({
          payment: {
            ...payment,
            paymentMethodType: payload.tab,
            paymentMethodId: null,
          },
        }));
        break;
      case 'updateHolderName':
        this.setState(({ payment }) => ({
          payment: {
            ...payment,
            accountHolderName: payload.accountHolderName || '',
          },
        }));
        break;
      case 'updatedPaymentMethodSelected':
        this.setState(({ payment }) => ({
          payment: {
            ...payment,
            paymentMethodId: payload.paymentMethodId,
          },
        }));
        break;
      case 'updatedUserPaymentMethods':
        this.setState(({ payment }) => ({
          payment: {
            ...payment,
            paymentMethods: payload.paymentMethods,
            paymentMethodId: payload.paymentMethodId,
          },
        }));
        break;
      case 'removePaymentMethod':
        this.setState(({ payment }) => ({
          payment: {
            ...payment,
            paymentMethods: payload.paymentMethods,
            paymentMethodId: payload.paymentMethodId,
          },
        }));
        break;
      default:
        console.log('*Default', type, payload);
    }
  }

  handleCountryChange(country) {
    this.setState(({ shipping }) => ({
      shipping: { ...shipping, address: { ...shipping.address, country } },
    }));
  }

  handleSectionAdd(section, productCard) {
    this.setState(
      ({ cart }) => ({
        cart: { ...cart, [section]: cart[section].concat(productCard) },
      }),
      this.checkDiscountApplication,
    );
  }

  handleSectionRemove(section, id) {
    this.setState(
      ({ cart }) => ({
        cart: {
          ...cart,
          [section]: cart[section].filter(({ product }) => product._id !== id),
        },
      }),
      this.checkDiscountApplication,
    );
  }

  handlePlanSelection(plan, callback) {
    this.setState(
      ({ cart }) => {
        let { subscriptions, products } = cart;
        subscriptions = subscriptions.filter(s => isFeatureAvailable(s, plan));
        products = products.filter(s => isFeatureAvailable(s, plan));
        return {
          selectedPlan: plan,
          cart: { ...cart, plan, subscriptions, products },
        };
      },
      () => {
        if (callback) {
          callback(this.state.cart);
        }
      },
    );
  }

  handleUserTotalCalculations(type, newValue) {
    this.setState(({ calculatedUserStats }) => ({
      calculatedUserStats: {
        totalUsers:
          type === 'users' ? newValue : calculatedUserStats.totalUsers,
        totalAdmins:
          type === 'admins' ? newValue : calculatedUserStats.totalAdmins,
      },
    }));
  }

  handlePackAdd(product) {
    this.handleSectionAdd('packs', { product, quantity: 1 });
  }

  handlePackRemove(id) {
    this.handleSectionRemove('packs', id);
  }

  handleSubscriptionAdd(product) {
    this.handleSectionAdd('subscriptions', product);
  }

  handleSubscriptionRemove(id) {
    this.handleSectionRemove('subscriptions', id);
  }

  handleProductAdd(product) {
    this.handleSectionAdd('products', product);
  }

  handleProductRemove(id) {
    this.handleSectionRemove('products', id);
  }

  handleDiscountAdd(discount) {
    const { t } = this.props;

    this.setState(({ cart }) => {
      if (cart.discounts.some(d => d._id === discount._id)) {
        Notifications.showNotificationError(
          t('error'),
          t('error_duplicateDiscountCode'),
        );
        return { cart };
      }
      if (cart.discounts.some(d => d.type === discount.type)) {
        Notifications.showNotificationError(
          t('error'),
          t(`error_discount_multiple_${discount.type}`),
        );
        return { cart };
      }
      return { cart: { ...cart, discounts: cart.discounts.concat(discount) } };
    }, this.checkDiscountApplication);
  }

  handleDiscountRemove(id) {
    this.setState(
      ({ cart }) => ({
        cart: {
          ...cart,
          discounts: cart.discounts.filter(discount => discount._id !== id),
        },
      }),
      this.checkDiscountApplication,
    );
  }

  handleSubmit(paymentMethods) {
    const { t, _csrf, isResellerInstance } = this.props;
    const {
      filledSteps,
      cart: { packs, products, subscriptions, discounts },
      billing: {
        billingAddress,
        businessType,
        invoiceEmail,
        taxId,
        taxCode,
        taxType,
        billingLanguage,
      },
      payment: { paymentMethodType, paymentMethodId },
      interval,
      plansAreFree,
      selectedPlan,
    } = this.state;

    const paymentIsNotRequired =
      plansAreFree && packs.length === 0 && products.length === 0;
    const firstNonFilled = () =>
      Object.keys(filledSteps).reduce((acc, step) => {
        const index = Number(step);
        if (filledSteps[index]) {
          return acc;
        }
        return index > acc ? acc : index;
      }, Number.MAX_SAFE_INTEGER);
    if (firstNonFilled() !== Number.MAX_SAFE_INTEGER) {
      this.handleNavigation({ paymentMethods }, firstNonFilled(), true, true);
    } else {
      const {
        firstName,
        lastName,
        companyName,
        phone,
        address,
        address_1,
        address_2,
        zipCode,
        city,
        state,
        country,
        countryCode,
        provinceCode,
        provinceCountryCode,
      } = billingAddress;

      const transformProducts = ({ product: p, quantity }) => ({
        item: p._id,
        quantity,
      });

      let params;
      if (isResellerInstance) {
        params = {
          _csrf,
          interval,
          plan: selectedPlan?._id,
          subscriptions: subscriptions.map(({ product: p }) => p._id),
          products: products.map(transformProducts),
          packs: packs.map(transformProducts),
        };
      } else {
        params = {
          _csrf,
          interval,
          billingAddress: {
            firstName,
            lastName,
            companyName,
            phone,
            email: invoiceEmail,
            address,
            address_1,
            address_2,
            zipCode,
            city,
            state,
            country,
            countryCode,
            provinceCode,
            provinceCountryCode,
          },
          businessType,
          invoiceEmail,
          taxId,
          taxCode,
          taxType,
          billingLanguage,
          plan: selectedPlan?._id,
          subscriptions: subscriptions.map(({ product: p }) => p._id),
          products: products.map(transformProducts),
          packs: packs.map(transformProducts),
          discounts: discounts.map(d => ({ _id: d._id, type: d.type })),
          paymentMethodType: !paymentIsNotRequired
            ? paymentMethodType
            : undefined,
          paymentMethodId: !paymentIsNotRequired ? paymentMethodId : undefined,
        };
      }

      const request = new Request(_csrf).put('/subscription', params);

      const errorKey = 'error_subscriptionUpdate';

      this.setState({ postingForm: true });
      request
        .done(() => {
          // if (!isEdit) {
          //   SubscriptionInfo.clearCache();
          // }
          window.location.href = '/';
        })
        .fail(err => {
          let error = t(errorKey);
          if (err && err.responseJSON) {
            error = err.responseJSON.message || t(err.responseJSON.error);
          }
          Notifications.showNotificationError(t('error'), error);
          this.setState({ postingForm: false });
        });
    }
  }

  async onIntervalChange(interval) {
    try {
      const { currentUserStats, activeDiscount } = this.props;
      const {
        cart: previousCart,
        interval: previousInterval,
        selectedPlan: previousSelectedPlan,
      } = this.state;
      const plans = await SubscriptionDetails.getListPlans(interval);
      const { categories, packs } = await SubscriptionDetails.getListProducts(
        interval,
      );
      const { propSubs } = serializeData(categories);

      const updatedPacks = previousCart.packs.filter(
        p => p.interval !== previousInterval,
      );
      const subs = propSubs;
      const prods = [];
      const selectedPlan =
        previousSelectedPlan || (plans.length > 0 && plans[0]);
      const cart = {
        numberUsers: currentUserStats?.totalUsers,
        numberAdmins: currentUserStats?.totalAdmins,
        plan: selectedPlan,
        subscriptions: subs,
        packs: updatedPacks,
        products: prods,
        discounts: activeDiscount ? [{ ...activeDiscount, applied: true }] : [],
      };
      this.setState({ interval, plans, categories, packs, cart, selectedPlan });
    } catch (err) {
      this.handleRequestError(err);
    }
  }

  attemptToNavigate(id) {
    if (this.currentStep && this.currentStep.validateAndLeave) {
      this.currentStep.validateAndLeave(id);
    } else {
      this.handleNavigation({}, id, true);
    }
  }

  checkDiscountApplication() {
    const { categories, plans, plansAreFree } = this.state;
    this.setState(({ cart }) => ({
      cart: {
        ...cart,
        discounts: markDiscountApplication(
          cart,
          categories,
          plans,
          plansAreFree,
        ),
      },
    }));
  }

  render() {
    const {
      t,
      currentUserStats,
      countries,
      isEdit,
      isExpired,
      isActive,
      awaitingValidation,
      isResellerInstance,
      taxIdTypes,
      businessTypes,
      taxRatePercentage,
      shouldCalculateActiveUsers,
      paymentMethodsTypes,
      license,
      redirectToPaymentSettings,
      languages,
    } = this.props;
    const { subscriptionPaymentStatus } = license;
    const {
      showErrors,
      selectedIndex,
      postingForm,
      cart,
      filledSteps,
      interval,
      categories,
      packs,
      plans,
      selectedPlan,
      calculatedUserStats,
      billing,
      payment: paymentSettings,
      plansAreFree,
    } = this.state;
    const noPayment =
      plansAreFree && cart.packs.length === 0 && cart.products.length === 0;
    const hasPaidSubscriptions = !plansAreFree && cart.subscriptions.length > 0;
    let finishButtonText = noPayment
      ? t('save')
      : t('subscriptionCreate_payment_payNow');
    if (hasPaidSubscriptions) {
      finishButtonText = t('subscriptionCreate_payment_save_subscription');
    }
    const editionDisabled =
      (isExpired || !isActive || awaitingValidation) &&
      !redirectToPaymentSettings;

    let message = t('subscriptionCreate_isInactive');
    if (isExpired) {
      message = t('subscriptionCreate_isExpired');
    }
    if (awaitingValidation) {
      message = t('subscriptionCreate_awaitingValidation');
    }
    const disabledMessage = !isActive
      ? t('subscriptionCreate_isInactive')
      : message;

    return (
      <>
        {editionDisabled && (
          <div className="edition-disabled-overlay">
            <span className="edition-disabled-message">
              <span>
                {disabledMessage}
                {isExpired && !awaitingValidation && (
                  <a href="subscription/create">{t('header_banner_upgrade')}</a>
                )}
                {!isActive && (
                  <a href="/payment/settings">
                    {t('subscriptionCreate_reactivated')}
                  </a>
                )}
              </span>
            </span>
          </div>
        )}
        <DocumentTitle title={t('subscription_detail_head_title')}>
          <ContentContainer>
            <div className="m-content">
              <div id="subscription_detail_container" className="create-wizard">
                <NavigationMenu
                  selectedIndex={selectedIndex}
                  plansAreFree={plansAreFree}
                  cart={cart}
                  plans={plans}
                  categories={categories}
                  packs={packs}
                  onSelectionChange={this.attemptToNavigate}
                  filledSteps={filledSteps}
                  onPackRemove={this.handlePackRemove}
                  onProductRemove={this.handleProductRemove}
                  onSubscriptionRemove={this.handleSubscriptionRemove}
                  onDiscountRemove={this.handleDiscountRemove}
                  interval={interval}
                  taxRatePercentage={taxRatePercentage}
                  isResellerInstance={isResellerInstance}
                />
                <div
                  className="create-wizard-content m-portlet"
                  id="stepContainer"
                >
                  {!isResellerInstance && selectedIndex === 1 && (
                    <Solutions
                      nextStep={selectedIndex + 1}
                      wrappedComponentRef={c => {
                        this.currentStep = c;
                      }}
                      currentUserStats={currentUserStats}
                      calculatedUserStats={calculatedUserStats}
                      onUserCalculationsChanged={
                        this.handleUserTotalCalculations
                      }
                      onNavigate={this.handleNavigation}
                      cart={cart}
                      plans={plans}
                      selectedPlan={selectedPlan}
                      onPlanChanged={this.handlePlanSelection}
                      isEdit={isEdit}
                      onProductAdd={this.handleProductAdd}
                      onProductRemove={this.handleProductRemove}
                      onSubscriptionAdd={this.handleSubscriptionAdd}
                      onSubscriptionRemove={this.handleSubscriptionRemove}
                      interval={interval}
                      shouldCalculateActiveUsers={shouldCalculateActiveUsers}
                      hasPermissionToChange={isHigherAdmin(
                        this.props.loggedUser,
                      )}
                    />
                  )}
                  {((!isResellerInstance && selectedIndex === 2) ||
                    (isResellerInstance && selectedIndex === 1)) && (
                    <BasicFeatures
                      nextStep={selectedIndex + 1}
                      previousStep={selectedIndex - 1}
                      wrappedComponentRef={c => {
                        this.currentStep = c;
                      }}
                      cart={cart}
                      packs={packs}
                      categories={categories}
                      onPackAdd={this.handlePackAdd}
                      onPackRemove={this.handlePackRemove}
                      onProductAdd={this.handleProductAdd}
                      onProductRemove={this.handleProductRemove}
                      onSubscriptionAdd={this.handleSubscriptionAdd}
                      onSubscriptionRemove={this.handleSubscriptionRemove}
                      onNavigate={this.handleNavigation}
                      isEdit={isEdit}
                      interval={interval}
                      currentUserStats={currentUserStats}
                      hasPermissionToChange
                    />
                  )}
                  {((!isResellerInstance && selectedIndex === 3) ||
                    (isResellerInstance && selectedIndex === 2)) && (
                    <AdvancedFeatures
                      nextStep={selectedIndex + 1}
                      previousStep={selectedIndex - 1}
                      wrappedComponentRef={c => {
                        this.currentStep = c;
                      }}
                      cart={cart}
                      packs={packs}
                      categories={categories}
                      onPackAdd={this.handlePackAdd}
                      onPackRemove={this.handlePackRemove}
                      onProductAdd={this.handleProductAdd}
                      onProductRemove={this.handleProductRemove}
                      onSubscriptionAdd={this.handleSubscriptionAdd}
                      onSubscriptionRemove={this.handleSubscriptionRemove}
                      onNavigate={this.handleNavigation}
                      isEdit={isEdit}
                      interval={interval}
                      currentUserStats={currentUserStats}
                      hasPermissionToChange
                    />
                  )}
                  {!isResellerInstance && selectedIndex === 4 && (
                    <Options
                      nextStep={selectedIndex + 1}
                      previousStep={selectedIndex - 1}
                      wrappedComponentRef={c => {
                        this.currentStep = c;
                      }}
                      cart={cart}
                      packs={packs}
                      categories={categories}
                      onPackAdd={this.handlePackAdd}
                      onPackRemove={this.handlePackRemove}
                      onProductAdd={this.handleProductAdd}
                      onProductRemove={this.handleProductRemove}
                      onSubscriptionAdd={this.handleSubscriptionAdd}
                      onSubscriptionRemove={this.handleSubscriptionRemove}
                      onNavigate={this.handleNavigation}
                      isEdit={isEdit}
                      interval={interval}
                      currentUserStats={currentUserStats}
                      hasPermissionToChange={isHigherAdmin(
                        this.props.loggedUser,
                      )}
                    />
                  )}
                  {!isResellerInstance && selectedIndex === 5 && (
                    <Billing
                      nextStep={selectedIndex + 1}
                      previousStep={selectedIndex - 1}
                      wrappedComponentRef={c => {
                        this.currentStep = c;
                      }}
                      countries={countries}
                      onNavigate={this.handleNavigation}
                      showValidationErrors={showErrors}
                      resetShowErrors={this.handleResetErrors}
                      onCountryChange={this.handleCountryChange}
                      isResellerInstance={isResellerInstance}
                      loggedUser={this.props.loggedUser}
                      fieldsRequired={!noPayment}
                      taxIdTypes={taxIdTypes}
                      billing={billing}
                      businessTypes={businessTypes}
                      languages={languages}
                    />
                  )}
                  {((!isResellerInstance && selectedIndex === 6) ||
                    (isResellerInstance && selectedIndex === 3)) && (
                    <Payment
                      wrappedComponentRef={c => {
                        this.currentStep = c;
                      }}
                      subscriptionPaymentStatus={subscriptionPaymentStatus}
                      paymentSettings={paymentSettings}
                      paymentMethodsTypes={paymentMethodsTypes}
                      cart={cart}
                      finishButtonText={finishButtonText}
                      onFinish={this.handleSubmit}
                      onNavigate={this.handleNavigation}
                      showValidationErrors={showErrors}
                      postingForm={postingForm}
                      plansAreFree={plansAreFree}
                      categories={categories}
                      packs={packs}
                      plans={plans}
                      billing={billing}
                      onPackRemove={this.handlePackRemove}
                      onProductRemove={this.handleProductRemove}
                      onSubscriptionRemove={this.handleSubscriptionRemove}
                      onDiscountAdd={this.handleDiscountAdd}
                      onDiscountRemove={this.handleDiscountRemove}
                      isEdit={isEdit}
                      isResellerInstance={isResellerInstance}
                      interval={interval}
                      taxRatePercentage={taxRatePercentage}
                      updatePaymentMethod={this.handlePaymentMethod}
                      handleIsLoading={this.handleLoading}
                    />
                  )}
                </div>
              </div>
            </div>
          </ContentContainer>
        </DocumentTitle>
      </>
    );
  }
}

SubscriptionDetails.propTypes = {
  /** Translation Function */
  t: PropTypes.func.isRequired,
  _csrf: PropTypes.string.isRequired,
  redirectToPaymentSettings: PropTypes.bool,
  license: PropTypes.shape(LicenseProp.propType),
  currentUserStats: PropTypes.shape({
    totalUsers: PropTypes.number,
    totalAdmins: PropTypes.number,
  }),
  billing: PropTypes.shape(BillingProp.propType),
  activeDiscount: PropTypes.shape(DiscountProp.propType),
  paymentMethods: PropTypes.arrayOf(
    PropTypes.shape(StripePaymentMethodsProp.propType),
  ),
  paymentMethodsTypes: PropTypes.arrayOf(PropTypes.string).isRequired,
  countries: PropTypes.arrayOf(PropTypes.shape(CountryProp.propType))
    .isRequired,
  categories: PropTypes.arrayOf(PropTypes.shape(CategoryProp.propType))
    .isRequired,
  packs: PropTypes.arrayOf(PropTypes.shape(PackProp.propType)).isRequired,
  plans: PropTypes.arrayOf(PropTypes.shape(PlanProp.propType)).isRequired,
  isResellerInstance: PropTypes.bool.isRequired,
  loggedUser: PropTypes.shape(UserProp.propType),
  isExpired: PropTypes.bool,
  isActive: PropTypes.bool,
  awaitingValidation: PropTypes.bool,
  isEdit: PropTypes.bool,
  interval: PropTypes.string.isRequired,
  taxIdTypes: PropTypes.arrayOf(PropTypes.shape(TaxIdTypeProp.propType))
    .isRequired,
  shouldCalculateActiveUsers: PropTypes.bool.isRequired,
  taxRatePercentage: PropTypes.number.isRequired,
  businessTypes: PropTypes.arrayOf(PropTypes.shape(BusinessTypeProp.propType))
    .isRequired,
  languages: PropTypes.arrayOf(PropTypes.shape(CountryProp.propType))
    .isRequired,
};

SubscriptionDetails.defaultProps = {
  redirectToPaymentSettings: false,
  isActive: true,
  isExpired: false,
  awaitingValidation: false,
  isEdit: false,
  paymentMethods: [],
  license: LicenseProp.defaultProp,
  currentUserStats: {
    totalUsers: 0,
    totalAdmins: 0,
  },
  billing: BillingProp.defaultProp,
  activeDiscount: null,
  loggedUser: null,
};

export default compose(
  translate,
  withCSRF,
  withLoggedUser,
)(SubscriptionDetails);
